import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, ShieldCheck, Lock } from "lucide-react";
import { API, formatApiError } from "@/lib/api";

const input = "w-full px-3 py-2 border-2 border-ink font-mono text-sm";

export default function Public1099() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setCtx((await axios.get(`${API}/public/1099/${token}`)).data); }
    catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const consent = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/1099/${token}/consent`, { accepted: true, signature_name: sig });
      toast.success("Consent recorded");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/public/1099/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `1099NEC-${ctx.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="hard-card p-8 max-w-lg font-mono text-sm" data-testid="pub1099-error">{error}</div>
    </div>
  );
  if (!ctx) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">LOADING…</div>;

  const locked = ctx.consent_required && !ctx.consented;

  return (
    <div className="min-h-screen py-10 px-4 flex justify-center">
      <div className="hard-card p-6 md:p-8 w-full max-w-2xl" data-testid="pub1099-page">
        <div className="overline mb-2">§ FORM 1099-NEC · TAX YEAR {ctx.year}</div>
        <div className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          {ctx.recipient_name}
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-2">
          Issued by {ctx.payer_name}. This page is unique to you — do not forward the link.
        </div>

        {!ctx.available ? (
          <div className="mt-6 border-2 border-ink p-4 font-mono text-sm" data-testid="pub1099-unavailable">
            No reportable Form 1099-NEC exists for you for {ctx.year}.
          </div>
        ) : locked ? (
          <div className="mt-6 border-2 border-ink p-4" data-testid="pub1099-consent-block">
            <div className="font-mono text-xs space-y-2">
              <div className="flex gap-2"><Lock className="w-3 h-3 shrink-0 mt-[2px]" />
                <span>The IRS requires your consent before a tax form is furnished
                  electronically instead of on paper.</span></div>
              <div>By consenting you agree to receive your {ctx.year} Form 1099-NEC
                electronically. You may withdraw consent at any time by contacting
                {" "}{ctx.payer_name}, and a paper copy will be mailed to you.</div>
            </div>
            <div className="mt-3">
              <span className="overline mb-2 text-[10px] block">Type your full legal name to consent</span>
              <input data-testid="pub1099-signature" className={input} value={sig}
                onChange={(e) => setSig(e.target.value)} />
            </div>
            <button onClick={consent} disabled={busy || !sig.trim()} data-testid="pub1099-consent-btn"
              className="hard-btn mt-3 py-2 px-5 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
              <ShieldCheck className="w-3 h-3" /> {busy ? "Recording…" : "I consent"}
            </button>
          </div>
        ) : (
          <div className="mt-6" data-testid="pub1099-download-block">
            <button onClick={download} disabled={busy} data-testid="pub1099-download"
              className="hard-btn py-3 px-6 font-mono uppercase text-[11px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
              <Download className="w-3 h-3" /> {busy ? "Preparing…" : "Download my 1099-NEC"}
            </button>
            {ctx.consented ? (
              <div className="mt-3 font-mono text-[10px] opacity-70" data-testid="pub1099-consent-note">
                Electronic delivery consent recorded {String(ctx.consent_at || "").slice(0, 10)}.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
